import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    setupSuperAdmin,
    clearError,
  } = useAuthStore();

  const loginAndRedirect = useCallback(async (email: string, password: string, orgId?: string) => {
    await login(email, password, orgId);
    navigate('/dashboard');
  }, [login, navigate]);

  const logoutAndRedirect = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const setupAndRedirect = useCallback(async (data: { secret: string; email: string; password: string; name?: string }) => {
    await setupSuperAdmin(data);
    navigate('/dashboard');
  }, [setupSuperAdmin, navigate]);

  const hasPermission = useCallback((requiredRole: 'user' | 'admin' | 'super_admin'): boolean => {
    if (!user) return false;

    const roleHierarchy: Record<string, number> = {
      'user': 1,
      'admin': 2,
      'super_admin': 3,
    };

    // Check roles array if available, otherwise fall back to role field
    const userMaxLevel = user.roles
      ? Math.max(...user.roles.map(r => roleHierarchy[r] || 1))
      : roleHierarchy[user.role];

    return userMaxLevel >= roleHierarchy[requiredRole];
  }, [user]);

  const isSuperAdmin = useCallback(() =>
    user?.role === 'super_admin' || user?.roles?.includes('super_admin') || false,
  [user]);

  const isAdmin = useCallback(() =>
    user?.role === 'admin' || user?.role === 'super_admin' ||
    user?.roles?.includes('admin') || user?.roles?.includes('super_admin') || false,
  [user]);

  return {
    user,
    isAuthenticated,
    loading,
    error,
    login: loginAndRedirect,
    logout: logoutAndRedirect,
    setupSuperAdmin: setupAndRedirect,
    clearError,
    hasPermission,
    isSuperAdmin,
    isAdmin,
  };
};
